import React, { useState } from "react";
import Link from "next/link";
import {
  InstagramLogoIcon,
  LinkedinLogoIcon,
  XLogoIcon,
} from "@phosphor-icons/react/dist/ssr";
import { useLanguage } from "@/contexts/LanguageContext";
import CalendlyModal from "@/components/CalendlyModal";

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <footer className="bg-gradient-to-b from-white to-blue-100 py-20 px-8 xl:px-0">
      <div className="max-w-6xl mx-auto flex flex-col gap-10">
        <div className="flex lg:flex-row flex-col gap-12 justify-between">
          <div className="lg:col-span-1 max-lg:flex max-lg:flex-col max-lg:text-center max-lg:items-center max-lg:justify-center">
            <h2 className="text-2xl text-gray-800 mb-6">
              {t("footer.tagline")}
            </h2>

            {/* Social Icons */}
            <div className="flex gap-4 lg:mt-8">
              <Link
                target="_blank"
                href="https://www.linkedin.com/company/featcalapp"
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-950 text-2xl hover:scale-110 transition-all duration-300"
                aria-label="Follow Featcal on LinkedIn"
              >
                <LinkedinLogoIcon />
              </Link>
              <Link
                target="_blank"
                href="https://x.com/featcal_com"
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-950 text-2xl hover:scale-110 transition-all duration-300"
                aria-label="Follow Featcal on Twitter"
              >
                <XLogoIcon />
              </Link>

              <Link
                target="_blank"
                href="https://www.instagram.com/featcal/"
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-950 text-2xl hover:scale-110 transition-all duration-300"
                aria-label="Follow Featcal on Instagram"
              >
                <InstagramLogoIcon />
              </Link>
            </div>
          </div>

          <div className="flex lg:flex-row flex-col lg:gap-24 gap-10 max-lg:items-center max-lg:text-center">
            <div>
              <h3 className="text-xs font-semibold text-gray-800 mb-4">
                {t("footer.navigation")}
              </h3>
              <nav className="space-y-3">
                <Link
                  href="/"
                  className="block text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t("footer.home")}
                </Link>
                <Link
                  href="#for-professionals"
                  className="block text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t("footer.forProfessionals")}
                </Link>
                <Link
                  href="#app-features"
                  className="block text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t("footer.appFeatures")}
                </Link>
              </nav>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-800 mb-4">
                {t("footer.support")}
              </h3>
              <nav className="space-y-3">
                <Link
                  href="#faq"
                  className="block text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t("footer.faq")}
                </Link>
                <Link
                  href="/blog"
                  className="block text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t("footer.blog")}
                </Link>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="block text-gray-600 hover:text-gray-800 transition-colors text-left"
                >
                  {t("footer.contact")}
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">{t("footer.copyright")}</p>
            <div className="flex gap-6 text-sm">
              <Link
                href="/privacy-policy"
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                {t("footer.privacyPolicy")}
              </Link>
              <Link
                href="/terms-conditions"
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                {t("footer.termsConditions")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Calendly Modal */}
      <CalendlyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </footer>
  );
};

export default Footer;
